import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SortDirection } from 'constants/sorting';
import { getClimateIndexLabel } from 'helper/Meteorology/climateIndicesLabels';
import {
  useGetClimateIndicesQuery,
  useGetFilteredYearsMapsMutation,
  useGenerateEnsembleMutation,
} from 'services/api/Meteorology/Weather/Similarity';
import {
  setTableData,
  setAnos,
  clearMaps,
  setMapsLoading,
  setMapsData,
  setEnsembleData,
  setEnsembleModalOpen,
  selectSimilaridadeTableData,
  selectSimilaridadeMapsData,
  selectSimilaridadeMapsLoading,
  selectSimilaridadeMapsGenerated,
  selectSimilaridadeMeses,
  selectSimilaridadeVariavel,
  selectSimilaridadeAnos,
  selectSimilaridadeCanGenerateMaps,
  selectSimilaridadeCanGenerateEnsemble,
} from 'redux/reducers/similaridadeReducer';
import { IndiceClimatico, IndiceClimaticoField, MapData } from 'types/Meteorology/indicesClimaticos';
import INDICES from 'components/Meteorology/Agro/Similarity/Mobile/indices';

export type SimilarityIndexFilters = Partial<Record<IndiceClimaticoField, string[]>>;

const EMPTY_FILTERS: SimilarityIndexFilters = {};

const yearKey = (ano: number, mes: number) => `${ano}-${mes}`;

export default function useSimilarityMobile() {
  const dispatch = useDispatch();
  const { data: apiData = [], isLoading, isError } = useGetClimateIndicesQuery();
  const [getFilteredMaps] = useGetFilteredYearsMapsMutation();
  const [generateEnsemble, { isLoading: isGeneratingEnsemble }] = useGenerateEnsembleMutation();

  const tableData = useSelector(selectSimilaridadeTableData);
  const mapsData = useSelector(selectSimilaridadeMapsData);
  const mapsGenerated = useSelector(selectSimilaridadeMapsGenerated);
  const isLoadingMaps = useSelector(selectSimilaridadeMapsLoading);
  const canGenerateMaps = useSelector(selectSimilaridadeCanGenerateMaps);
  const canGenerateEnsembleRaw = useSelector(selectSimilaridadeCanGenerateEnsemble);
  const variavel = useSelector(selectSimilaridadeVariavel);
  const anos = useSelector(selectSimilaridadeAnos);
  const meses = useSelector(selectSimilaridadeMeses);
  const [month] = meses;

  useEffect(() => {
    if (apiData && apiData.length > 0) {
      dispatch(setTableData(apiData));
    }
  }, [apiData, dispatch]);

  const data = tableData.length > 0 ? tableData : apiData;

  const [filters, setFilters] = useState<SimilarityIndexFilters>(EMPTY_FILTERS);
  const [sortField, setSortField] = useState<IndiceClimaticoField | 'ano'>('ano');
  const [sortDirection, setSortDirection] = useState<SortDirection>('ASC');
  const [excludedYears, setExcludedYears] = useState<Set<string>>(new Set());

  useEffect(() => {
    dispatch(clearMaps());
    setExcludedYears(new Set());
  }, [filters, month, sortField, sortDirection, dispatch]);

  const toggleYearMap = (ano: number, mes: number) => {
    setExcludedYears((prev) => {
      const key = yearKey(ano, mes);
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const activeMapsData = useMemo(
    () => mapsData.filter((map) => !excludedYears.has(yearKey(map.ano, map.mes))),
    [mapsData, excludedYears],
  );

  const canGenerateEnsemble = canGenerateEnsembleRaw && activeMapsData.length > 0;

  const toggleFilter = (indexId: IndiceClimaticoField, label: string) => {
    setFilters((prev) => {
      const current = prev[indexId] || [];
      const next = current.includes(label)
        ? current.filter((item) => item !== label)
        : [...current, label];
      return { ...prev, [indexId]: next };
    });
  };

  const clearFilters = () => setFilters(EMPTY_FILTERS);

  const activeFilterCount = Object.values(filters)
    .reduce((sum, selected) => sum + (selected?.length || 0), 0);

  const rows = useMemo<IndiceClimatico[]>(() => {
    let filtered = month ? data.filter((item) => item.mes === month) : [...data];

    if (mapsData.length > 0) {
      filtered = filtered.filter((item) => mapsData.some(
        (map) => map.ano === item.ano && map.mes === item.mes,
      ));
    }

    INDICES.forEach(({ id, type }) => {
      const selected = filters[id];
      if (selected && selected.length > 0) {
        filtered = filtered.filter((item) => selected.includes(
          getClimateIndexLabel(item[id], type),
        ));
      }
    });

    return [...filtered].sort((a, b) => {
      const comparison = sortField === 'ano' ? a.ano - b.ano : a[sortField] - b[sortField];
      return sortDirection === 'ASC' ? comparison : -comparison;
    });
  }, [data, mapsData, filters, month, sortField, sortDirection]);

  useEffect(() => {
    if (rows.length > 0) {
      dispatch(setAnos(Array.from(new Set(rows.map((item) => item.ano)))));
    }
  }, [rows, dispatch]);

  const handleGenerateMaps = async () => {
    if (!canGenerateMaps) return;

    setExcludedYears(new Set());
    dispatch(setMapsLoading(true));

    try {
      const promises = meses.map((mes) => getFilteredMaps({ variavel, mes, anos }).unwrap()
        .then((result) => {
          if (result && result.length > 0) {
            return result.map((map) => ({
              nome: map.nome,
              ano: map.ano,
              mes,
              base64_data: '',
              link: map.link,
              variavel,
            }));
          }
          return [];
        })
        .catch(() => []));

      const results = await Promise.all(promises);
      const allMaps: MapData[] = results.flat();

      dispatch(setMapsData(allMaps));
    } catch (error) {
      dispatch(setMapsLoading(false));
    }
  };

  const handleGenerateEnsemble = async () => {
    if (!canGenerateEnsemble) return;

    const anosEnsemble = Array.from(new Set(activeMapsData.map((map) => map.ano)))
      .map((ano) => Number(ano))
      .sort((a, b) => a - b);
    const mesEnsemble = activeMapsData[0]?.mes || 1;

    try {
      const result = await generateEnsemble({
        dict_request: {
          variavel,
          mes: Number(mesEnsemble),
          anos: anosEnsemble,
        },
        email: '',
      }).unwrap();

      if (result && result.url) {
        dispatch(setEnsembleData({
          nome: `Ensemble ${variavel}`,
          variavel: result.var || variavel,
          mes: typeof result.mes === 'string' ? parseInt(result.mes, 10) : Number(result.mes) || mesEnsemble,
          anos: Array.isArray(result.anos) ? result.anos : anosEnsemble,
          base64_data: '',
          url: result.url,
        } as any));
        dispatch(setEnsembleModalOpen(true));
      } else {
        // eslint-disable-next-line no-alert
        alert('Resposta da API não contém URL da imagem.');
      }
    } catch (error: any) {
      const errorMessage = error?.data?.message || error?.message || 'Erro ao gerar ensemble. Por favor, tente novamente.';
      // eslint-disable-next-line no-alert
      alert(errorMessage);
    }
  };

  let selectionLabel = `${rows.length} ano${rows.length !== 1 ? 's' : ''} listado${rows.length !== 1 ? 's' : ''}`;
  if (month) {
    selectionLabel = mapsGenerated
      ? `${activeMapsData.length} ano${activeMapsData.length !== 1 ? 's' : ''} com mapa`
      : `${rows.length} ano${rows.length !== 1 ? 's' : ''} selecionado${rows.length !== 1 ? 's' : ''}`;
  }

  return {
    isLoading,
    isError,
    month,
    rows,
    filters,
    toggleFilter,
    clearFilters,
    activeFilterCount,
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    mapsData,
    activeMapsData,
    excludedYears,
    toggleYearMap,
    mapsGenerated,
    isLoadingMaps,
    canGenerateMaps,
    canGenerateEnsemble,
    isGeneratingEnsemble,
    handleGenerateMaps,
    handleGenerateEnsemble,
    selectionLabel,
  };
}
